import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, MessageCircle, Share2, Plus, X, Image, Type, Send, Bookmark, ChevronLeft, ChevronRight, Search, Eye, MoreHorizontal, Smile } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEXT_BG_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#0ea5e9', '#18181b', '#1e3a5f', '#3d1f00', '#0c4a3e', '#4a1942', '#1a1a2e', '#2d1b69', '#0d3b66'];
const QUICK_EMOJIS = ['❤️', '🔥', '👏', '😂', '😍', '🙌', '💯', '🎉', '👀', '🤔'];

export default function FeedTab({ user, showToast, onProfileClick }) {
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showStoryCreate, setShowStoryCreate] = useState(false);
  const [viewingStory, setViewingStory] = useState(null);
  const [storyIdx, setStoryIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [fullScreenPost, setFullScreenPost] = useState(null);
  const [likersModal, setLikersModal] = useState(null);
  const [likersList, setLikersList] = useState([]);
  const [storyViewers, setStoryViewers] = useState(null);
  const [viewersList, setViewersList] = useState([]);
  const storiesRef = useRef(null);
  const storyTimerRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  // Listen for "new content" button from top bar
  useEffect(() => {
    const handler = () => setShowCreate(true);
    window.addEventListener('open-new-post', handler);
    return () => window.removeEventListener('open-new-post', handler);
  }, []);

  const fetchData = async () => {
    try {
      const [feedRes, storiesRes] = await Promise.all([
        fetch(`${API_URL}/api/feed`, { headers }),
        fetch(`${API_URL}/api/stories`, { headers }),
      ]);
      if (feedRes.ok) { const d = await feedRes.json(); const p = d.posts || []; setAllPosts(p); setPosts(p); }
      if (storiesRes.ok) {
        const storyData = await storiesRes.json();
        // Backend already returns grouped data: [{user_id, user_name, stories: [...], has_unseen}, ...]
        setStories(Array.isArray(storyData) ? storyData : []);
      }
    } catch {}
    setLoading(false);
  };

  const handleLike = async (postId) => {
    const res = await fetch(`${API_URL}/api/posts/${postId}/like`, { method: 'POST', headers });
    if (res.ok) {
      const d = await res.json();
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, likes_count: p.likes_count + (d.liked ? 1 : -1) } : p));
    }
  };

  const handleShare = async (post) => {
    try {
      const res = await fetch(`${API_URL}/api/share/link/post/${post.post_id}`);
      const d = res.ok ? await res.json() : {};
      const url = d.share_url || `${window.location.origin}/app?post=${post.post_id}`;
      if (navigator.share) {
        navigator.share({ title: d.title || `${post.user_name} on Connect`, text: post.content?.substring(0, 100), url });
      } else {
        navigator.clipboard.writeText(url);
        if (showToast) showToast('Share link copied!');
      }
    } catch {
      const url = `${window.location.origin}/app?post=${post.post_id}`;
      navigator.clipboard.writeText(url);
      if (showToast) showToast('Link copied!');
    }
  };

  const handleComment = async (postId, text) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comment?text=${encodeURIComponent(text)}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
        if (showToast) showToast('Comment posted!');
        return true;
      }
    } catch {}
    return false;
  };

  const fetchLikers = async (postId) => {
    setLikersModal(postId);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/likers`, { headers });
      if (res.ok) setLikersList(await res.json());
    } catch {}
  };

  const fetchStoryViewers = async (storyId) => {
    setStoryViewers(storyId);
    try {
      const res = await fetch(`${API_URL}/api/stories/${storyId}/viewers`, { headers });
      if (res.ok) setViewersList(await res.json());
    } catch {}
  };

  const handleSave = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/save`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        if (showToast) showToast(d.saved ? 'Post saved!' : 'Post unsaved');
        return d.saved;
      }
    } catch {}
    return false;
  };

  const viewStory = async (storyGroup, idx = 0) => {
    setViewingStory(storyGroup);
    setStoryIdx(idx);
    const s = storyGroup.stories[idx];
    if (s) await fetch(`${API_URL}/api/stories/${s.story_id}/view`, { method: 'POST', headers });
  };

  // Search filter effect
  useEffect(() => {
    if (!searchQuery.trim()) { setPosts(allPosts); return; }
    const q = searchQuery.toLowerCase();
    setPosts(allPosts.filter(p =>
      (p.content || '').toLowerCase().includes(q) ||
      (p.user_name || '').toLowerCase().includes(q)
    ));
  }, [searchQuery, allPosts]);

  // Story auto-advance
  useEffect(() => {
    if (!viewingStory) return;
    if (storyTimerRef.current) clearInterval(storyTimerRef.current);
    storyTimerRef.current = setInterval(() => {
      setStoryIdx(prev => {
        const next = prev + 1;
        if (next >= viewingStory.stories.length) {
          setViewingStory(null);
          return 0;
        }
        // Mark as viewed
        const s = viewingStory.stories[next];
        if (s) fetch(`${API_URL}/api/stories/${s.story_id}/view`, { method: 'POST', headers }).catch(() => {});
        return next;
      });
    }, 5000);
    return () => { if (storyTimerRef.current) clearInterval(storyTimerRef.current); };
  }, [viewingStory, headers]);

  if (loading) return <div className="h-full flex items-center justify-center"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="h-full overflow-y-auto bg-black" data-testid="feed-tab">
      {/* Search Bar */}
      <div className="px-4 pt-3 pb-2" data-testid="feed-search-bar">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search posts and people..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-full py-2 pl-9 pr-4 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
            data-testid="feed-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          )}
        </div>
      </div>

      {/* Stories Row */}
      <div className="relative border-b border-zinc-800" data-testid="stories-row">
        <div ref={storiesRef} className="flex items-center space-x-4 px-4 py-3 overflow-x-auto no-scrollbar">
          <button onClick={() => setShowStoryCreate(true)} className="flex flex-col items-center space-y-1 shrink-0" data-testid="add-story-btn">
            <div className="w-16 h-16 rounded-full border-2 border-dashed border-zinc-600 flex items-center justify-center">
              <Plus className="w-6 h-6 text-zinc-400" />
            </div>
            <span className="text-[10px] text-zinc-500">Your story</span>
          </button>
          {stories.map(sg => (
            <button key={sg.user_id} onClick={() => viewStory(sg)} className="flex flex-col items-center space-y-1 shrink-0" data-testid={`story-${sg.user_id}`}>
              <div className={`w-16 h-16 rounded-full p-[2px] ${sg.has_unseen ? 'bg-gradient-to-br from-purple-500 to-orange-500' : 'bg-zinc-700'}`}>
                <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                  {sg.user_picture ? <img src={sg.user_picture} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-sm font-bold">{sg.user_name?.[0]}</span>}
                </div>
              </div>
              <span className="text-[10px] text-zinc-400 max-w-[64px] truncate">{sg.user_name?.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Posts */}
      <div className="pb-4">
        {posts.map(post => (
          <PostCard key={post.post_id} post={post} onLike={handleLike} onShare={handleShare} onComment={handleComment} onSave={handleSave} onProfileClick={onProfileClick} onLikersClick={fetchLikers} onFullScreen={setFullScreenPost} />
        ))}
        {posts.length === 0 && <div className="text-center py-20 text-zinc-500"><p>No posts yet. Follow people or create your first post!</p></div>}
      </div>

      {/* Create Post Modal */}
      {showCreate && <CreatePostModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} user={user} />}
      {showStoryCreate && <CreateStoryModal onClose={() => setShowStoryCreate(false)} onCreated={() => { setShowStoryCreate(false); fetchData(); }} />}

      {/* Story Viewer with auto-advance */}
      {viewingStory && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="story-viewer" onClick={() => setViewingStory(null)}>
          <div className="relative w-full max-w-md h-full" onClick={e => e.stopPropagation()}>
            {/* Progress bars */}
            <div className="absolute top-2 left-2 right-2 z-10 flex space-x-1">
              {viewingStory.stories.map((_, i) => (
                <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-[5000ms] linear ${i < storyIdx ? 'w-full bg-white' : i === storyIdx ? 'w-full bg-white animate-[grow_5s_linear]' : 'w-0'}`}
                    style={i === storyIdx ? { animation: 'grow 5s linear forwards' } : {}} />
                </div>
              ))}
            </div>
            <style>{`@keyframes grow { from { width: 0% } to { width: 100% } }`}</style>
            <div className="absolute top-6 left-4 z-10 flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-zinc-700 overflow-hidden">
                {viewingStory.user_picture ? <img src={viewingStory.user_picture} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-xs font-bold">{viewingStory.user_name?.[0]}</div>}
              </div>
              <span className="text-sm font-semibold">{viewingStory.user_name}</span>
              <span className="text-[10px] text-zinc-400">
                {viewingStory.stories[storyIdx]?.created_at ? new Date(viewingStory.stories[storyIdx].created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}
              </span>
            </div>
            <button onClick={() => setViewingStory(null)} className="absolute top-6 right-4 z-10"><X className="w-6 h-6" /></button>
            {(() => {
              const s = viewingStory.stories[storyIdx];
              if (!s) return null;
              const overlayText = s.overlay_text || s.content || '';
              const overlayStyle = s.overlay_text_style || {};
              const caption = s.caption || '';
              return (
                <div className="w-full h-full flex flex-col">
                  {/* Media + Overlay container */}
                  <div className="flex-1 relative overflow-hidden">
                    {s.media_path ? (
                      <img src={s.media_path.startsWith('http') ? s.media_path : `${API_URL}/api/storage/${s.media_path}`} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: s.bg_color || '#6366f1' }} />
                    )}
                    {/* Overlay text positioned on top of media */}
                    {overlayText && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        style={overlayStyle.x !== undefined ? { position: 'absolute', left: `${overlayStyle.x || 50}%`, top: `${overlayStyle.y || 50}%`, transform: 'translate(-50%, -50%)' } : {}}>
                        <p className="text-white text-center font-bold px-6 drop-shadow-lg"
                          style={{ fontSize: overlayStyle.fontSize || (s.media_path ? '18px' : '28px'), color: overlayStyle.color || 'white', fontWeight: overlayStyle.fontWeight || 'bold' }}>
                          {overlayText}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Caption below media */}
                  {caption && (
                    <div className="px-4 py-3 bg-black/80 backdrop-blur-sm shrink-0">
                      <p className="text-sm text-white">{caption}</p>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Tap zones for prev/next */}
            <div className="absolute inset-0 flex z-[5]">
              <div className="w-1/3 h-full" onClick={() => { if (storyIdx > 0) setStoryIdx(storyIdx - 1); }} />
              <div className="w-1/3 h-full" />
              <div className="w-1/3 h-full" onClick={() => {
                if (storyIdx < viewingStory.stories.length - 1) {
                  const next = storyIdx + 1;
                  setStoryIdx(next);
                  const s = viewingStory.stories[next];
                  if (s) fetch(`${API_URL}/api/stories/${s.story_id}/view`, { method: 'POST', headers }).catch(() => {});
                } else { setViewingStory(null); }
              }} />
            </div>
            {storyIdx > 0 && <button onClick={() => setStoryIdx(storyIdx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10"><ChevronLeft className="w-8 h-8 text-white/50" /></button>}
            {storyIdx < viewingStory.stories.length - 1 && <button onClick={() => { setStoryIdx(storyIdx + 1); viewStory(viewingStory, storyIdx + 1); }} className="absolute right-2 top-1/2 -translate-y-1/2 z-10"><ChevronRight className="w-8 h-8 text-white/50" /></button>}
            {/* Story Viewers (own stories) */}
            {viewingStory.user_id === user?.user_id && (
              <button onClick={() => fetchStoryViewers(viewingStory.stories[storyIdx]?.story_id)}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-1.5"
                data-testid="story-viewers-btn">
                <Eye className="w-4 h-4 text-white" />
                <span className="text-xs text-white font-medium">{viewingStory.stories[storyIdx]?.views?.length || 0} viewers</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Full Screen Post Modal */}
      {fullScreenPost && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" onClick={() => setFullScreenPost(null)} data-testid="fullscreen-post">
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 backdrop-blur-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => { setFullScreenPost(null); onProfileClick?.(fullScreenPost.user_id); }}>
              <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden">
                {fullScreenPost.user_picture ? <img src={fullScreenPost.user_picture} alt="" className="w-full h-full object-cover" /> :
                  <span className="w-full h-full flex items-center justify-center text-xs font-bold">{fullScreenPost.user_name?.[0]}</span>}
              </div>
              <span className="text-sm font-bold text-white">{fullScreenPost.user_name}</span>
            </div>
            <button onClick={() => setFullScreenPost(null)}><X className="w-6 h-6 text-white" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4" onClick={e => e.stopPropagation()}>
            {fullScreenPost.media_path ? (
              <img src={fullScreenPost.media_path.startsWith('http') ? fullScreenPost.media_path : `${API_URL}/api/storage/${fullScreenPost.media_path}`} alt="" className="max-w-full max-h-full object-contain rounded-xl" />
            ) : (
              <div className="w-full max-w-lg rounded-2xl p-8" style={{ backgroundColor: fullScreenPost.bg_color || '#18181b' }}>
                <p className="text-white text-2xl font-bold text-center leading-relaxed">{fullScreenPost.content}</p>
              </div>
            )}
          </div>
          {fullScreenPost.content && fullScreenPost.media_path && (
            <div className="px-4 py-3 bg-zinc-900/80 backdrop-blur-sm" onClick={e => e.stopPropagation()}>
              <p className="text-sm text-white"><span className="font-bold mr-1">{fullScreenPost.user_name}</span>{fullScreenPost.content}</p>
            </div>
          )}
        </div>
      )}

      {/* Likers Modal */}
      {likersModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => { setLikersModal(null); setLikersList([]); }} data-testid="likers-modal">
          <div className="bg-zinc-900 w-full max-w-sm rounded-2xl border border-zinc-800 max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="font-bold text-white text-sm">Liked by</h3>
              <button onClick={() => { setLikersModal(null); setLikersList([]); }}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {likersList.length === 0 ? (
                <p className="text-center text-zinc-500 text-xs py-8">No likes yet</p>
              ) : likersList.map(u => (
                <div key={u.user_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 cursor-pointer" onClick={() => { setLikersModal(null); onProfileClick?.(u.user_id); }}
                  data-testid={`liker-${u.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="w-full h-full flex items-center justify-center text-xs font-bold">{u.name?.[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                    <p className="text-[10px] text-zinc-500">@{u.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Story Viewers Modal */}
      {storyViewers && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => { setStoryViewers(null); setViewersList([]); }} data-testid="viewers-modal">
          <div className="bg-zinc-900 w-full max-w-sm rounded-2xl border border-zinc-800 max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="font-bold text-white text-sm flex items-center gap-1.5"><Eye className="w-4 h-4" /> Viewers</h3>
              <button onClick={() => { setStoryViewers(null); setViewersList([]); }}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {viewersList.length === 0 ? (
                <p className="text-center text-zinc-500 text-xs py-8">No views yet</p>
              ) : viewersList.map(u => (
                <div key={u.user_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 cursor-pointer" onClick={() => { setStoryViewers(null); setViewingStory(null); onProfileClick?.(u.user_id); }}
                  data-testid={`viewer-${u.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="w-full h-full flex items-center justify-center text-xs font-bold">{u.name?.[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                    <p className="text-[10px] text-zinc-500">@{u.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──── Post Card (clean display, no twitter-style in feed) ──── */
function PostCard({ post, onLike, onShare, onComment, onSave, onProfileClick, onLikersClick, onFullScreen }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const QUICK_EMOJIS = ['😊', '❤️', '🔥', '👏', '💡', '🎯', '💯', '🙌', '😂', '🤔', '👍', '✨'];
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const renderTextWithHashtags = (text) => {
    if (!text) return null;
    const parts = text.split(/(#\w+)/g);
    return parts.map((part, i) =>
      part.startsWith('#') ? <span key={i} className="text-purple-400 font-semibold cursor-pointer hover:underline">{part}</span> : part
    );
  };

  const toggleComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      try {
        const res = await fetch(`${API_URL}/api/posts/${post.post_id}/comments`, { headers });
        if (res.ok) setComments(await res.json());
      } catch {}
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    const ok = await onComment(post.post_id, commentText);
    if (ok) {
      setCommentText('');
      try {
        const res = await fetch(`${API_URL}/api/posts/${post.post_id}/comments`, { headers });
        if (res.ok) setComments(await res.json());
      } catch {}
    }
  };

  const toggleSave = async () => {
    const result = await onSave(post.post_id);
    setSaved(result);
  };

  return (
    <div className="border-b border-zinc-800" data-testid={`post-${post.post_id}`}>
      {/* Post Header */}
      <div className="flex items-center space-x-3 px-4 py-3 cursor-pointer" onClick={() => onProfileClick && onProfileClick(post.user_id)} data-testid={`profile-click-${post.user_id}`}>
        <div className={`w-9 h-9 rounded-full shrink-0 p-[1.5px] ${post.is_starter ? 'bg-gradient-to-br from-emerald-500 to-teal-400' : 'bg-gradient-to-br from-purple-500 to-orange-400'}`}>
          <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
            {post.user_picture ? <img src={post.user_picture} alt="" className="w-full h-full object-cover" /> :
              <span className="text-[10px] font-bold">{post.user_name?.[0]}</span>}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate hover:text-purple-400 transition-colors">{post.user_name}</p>
          {post.tagged_expertise?.length > 0 && <p className="text-[10px] text-zinc-500">{post.tagged_expertise.join(' · ')}</p>}
        </div>
      </div>

      {/* Post Content — click for full screen */}
      {post.media_path ? (
        <div className="w-full aspect-square bg-zinc-900 relative cursor-pointer" onClick={() => onFullScreen?.(post)} data-testid={`post-media-${post.post_id}`}>
          <img src={post.media_path.startsWith('http') ? post.media_path : `${API_URL}/api/storage/${post.media_path}`} alt="" className="w-full h-full object-cover" />
        </div>
      ) : post.content ? (
        <div className="mx-4 rounded-2xl px-5 py-5 cursor-pointer" style={{ backgroundColor: post.bg_color || '#18181b' }} onClick={() => onFullScreen?.(post)} data-testid={`post-text-${post.post_id}`}>
          <p className={`leading-relaxed ${post.bg_color && post.bg_color !== '#18181b' ? 'text-white text-lg font-semibold' : 'text-zinc-200 text-sm'}`}>
            {renderTextWithHashtags(post.content)}
          </p>
        </div>
      ) : null}

      {/* Actions */}
      <div className="px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {(post.owner_privacy?.like !== false) && <button onClick={() => onLike(post.post_id)} className="flex items-center space-x-1 text-zinc-400 hover:text-red-400 transition-colors" data-testid={`like-${post.post_id}`}>
              <Heart className={`w-6 h-6 ${post.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
            </button>}
            {(post.owner_privacy?.like !== false) && <button onClick={() => onLikersClick?.(post.post_id)} className="text-xs text-zinc-400 hover:text-white" data-testid={`like-count-${post.post_id}`}>{post.likes_count || 0}</button>}
            {(post.owner_privacy?.comment !== false) && <button onClick={toggleComments} className="flex items-center space-x-1 text-zinc-400 hover:text-white transition-colors" data-testid={`comment-btn-${post.post_id}`}>
              <MessageCircle className="w-6 h-6" /><span className="text-xs">{post.comments_count || 0}</span>
            </button>}
            {(post.owner_privacy?.share !== false) && <button onClick={() => onShare(post)} className="text-zinc-400 hover:text-white transition-colors" data-testid={`share-${post.post_id}`}>
              <Share2 className="w-6 h-6" />
            </button>}
          </div>
          {(post.owner_privacy?.post_save !== false) && (
            <button onClick={toggleSave} className={`transition-colors ${saved ? 'text-white' : 'text-zinc-400 hover:text-white'}`} data-testid={`save-${post.post_id}`}>
              <Bookmark className={`w-6 h-6 ${saved ? 'fill-white' : ''}`} />
            </button>
          )}
        </div>
        {post.content && post.media_path && <p className="text-sm mt-2"><span className="font-semibold mr-1">{post.user_username || post.user_name}</span>{renderTextWithHashtags(post.content)}</p>}
        <p className="text-[10px] text-zinc-600 mt-1">{new Date(post.created_at).toLocaleDateString()}</p>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 pb-3 border-t border-zinc-900" data-testid={`comments-section-${post.post_id}`}>
          {loadingComments ? (
            <div className="py-3 text-center text-zinc-500 text-xs">Loading comments...</div>
          ) : (
            <>
              {comments.length > 0 ? (
                <div className="space-y-2 py-2 max-h-48 overflow-y-auto">
                  {comments.map((c, i) => (
                    <div key={c.comment_id || i} className="flex items-start space-x-2">
                      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 text-[9px] font-bold overflow-hidden cursor-pointer"
                        onClick={() => onProfileClick && onProfileClick(c.user_id)} data-testid={`comment-profile-${c.user_id || i}`}>
                        {c.user_picture ? <img src={c.user_picture} alt="" className="w-full h-full object-cover" /> : c.user_name?.[0]}
                      </div>
                      <div>
                        <p className="text-xs"><span className="font-semibold text-white cursor-pointer hover:text-purple-400" onClick={() => onProfileClick && onProfileClick(c.user_id)}>{c.user_name}</span> <span className="text-zinc-400">{c.text}</span></p>
                        <p className="text-[9px] text-zinc-600">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-600 py-2">No comments yet. Be the first!</p>
              )}
              {/* Emoji Reactions Row */}
              <div className="flex items-center gap-1 mb-1.5 flex-wrap" data-testid="emoji-reactions">
                {QUICK_EMOJIS.map(e => (
                  <button key={e} onClick={() => setCommentText(prev => prev + e)}
                    className="w-7 h-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-sm transition-colors hover:scale-110"
                    data-testid={`emoji-${e}`}>{e}</button>
                ))}
              </div>
              <div className="flex items-center space-x-2">
                <input value={commentText} onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitComment()}
                  placeholder="Add a comment..." className="flex-1 bg-zinc-900 rounded-xl px-3 py-2 text-xs outline-none border border-zinc-800 text-white placeholder-zinc-600"
                  data-testid={`comment-input-${post.post_id}`} />
                <button onClick={submitComment} disabled={!commentText.trim()} className="text-white disabled:text-zinc-600" data-testid={`comment-submit-${post.post_id}`}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePostModal({ onClose, onCreated, user }) {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('text');
  const [bgColor, setBgColor] = useState(TEXT_BG_COLORS[0]);
  const [file, setFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const token = localStorage.getItem('token');

  const handlePost = async () => {
    if (!content && !file) return;
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('post_type', file ? 'image' : 'text');
      formData.append('bg_color', !file ? bgColor : '');
      if (file) formData.append('file', file);
      const res = await fetch(`${API_URL}/api/posts`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData,
      });
      if (res.ok) onCreated();
    } catch {}
    setPosting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-5 border border-zinc-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-post-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-white">New Post</h3>
          <div className="flex items-center space-x-3">
            <button onClick={() => setPostType('text')} className={`p-2 rounded-lg ${postType === 'text' ? 'bg-zinc-700' : 'bg-zinc-800'}`} data-testid="type-text"><Type className="w-5 h-5" /></button>
            <button onClick={() => setPostType('image')} className={`p-2 rounded-lg ${postType === 'image' ? 'bg-zinc-700' : 'bg-zinc-800'}`} data-testid="type-image"><Image className="w-5 h-5" /></button>
          </div>
        </div>
        {postType === 'text' && (
          <>
            {/* Live preview — Twitter-screenshot style */}
            <div className="rounded-2xl overflow-hidden border border-zinc-700 mb-3" style={{ backgroundColor: bgColor }}>
              <div className="flex items-center space-x-2.5 px-4 pt-4 pb-2">
                <div className="w-8 h-8 rounded-full bg-black/20 flex items-center justify-center overflow-hidden shrink-0">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-xs font-bold text-white/80">{user?.name?.[0]}</span>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">{user?.name || 'You'}</p>
                  <p className="text-[10px] text-white/50">@{user?.username || 'you'}</p>
                </div>
              </div>
              <div className="px-4 pb-4 pt-1 min-h-[120px] flex items-center">
                <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Share a knowledge insight..." className="w-full bg-transparent text-white text-lg font-medium outline-none resize-none placeholder:text-white/40" rows={4} data-testid="post-content-input" />
              </div>
              <div className="border-t border-white/10 px-4 py-2">
                <p className="text-[10px] text-white/40">Now · Connect</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              {TEXT_BG_COLORS.map(c => (
                <button key={c} onClick={() => setBgColor(c)} className={`w-7 h-7 rounded-full border-2 ${bgColor === c ? 'border-white scale-110' : 'border-transparent'} transition-all`} style={{ backgroundColor: c }} />
              ))}
            </div>
          </>
        )}
        {postType === 'image' && (
          <>
            <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write a caption..." className="w-full bg-zinc-800 rounded-xl px-4 py-3 text-sm outline-none mb-3 border border-zinc-700" rows={2} data-testid="post-caption-input" />
            <label className="block w-full bg-zinc-800 border border-dashed border-zinc-600 rounded-xl p-8 text-center cursor-pointer mb-3 hover:bg-zinc-750 transition-colors">
              <Image className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
              <span className="text-sm text-zinc-400">{file ? file.name : 'Tap to upload photo or video'}</span>
              <input type="file" accept="image/*,video/*" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="post-file-input" />
            </label>
          </>
        )}

        <button onClick={handlePost} disabled={posting || (!content && !file)}
          className="w-full bg-white text-black font-bold rounded-full py-3 text-sm disabled:opacity-30 flex items-center justify-center space-x-2" data-testid="submit-post-btn">
          <Send className="w-4 h-4" /><span>{posting ? 'Posting...' : 'Share'}</span>
        </button>
      </div>
    </div>
  );
}

function CreateStoryModal({ onClose, onCreated }) {
  const [content, setContent] = useState('');
  const [caption, setCaption] = useState('');
  const [bgColor, setBgColor] = useState(TEXT_BG_COLORS[0]);
  const [file, setFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const token = localStorage.getItem('token');

  const handleCreate = async () => {
    if (!content && !file) return;
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('overlay_text', content);
      formData.append('caption', caption);
      formData.append('bg_color', bgColor);
      formData.append('overlay_text_style', JSON.stringify({ fontSize: '28px', color: 'white', fontWeight: 'bold' }));
      if (file) formData.append('file', file);
      const res = await fetch(`${API_URL}/api/stories`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData,
      });
      if (res.ok) { onCreated(); return; }
    } catch {}
    setPosting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-5 border border-zinc-800" onClick={e => e.stopPropagation()} data-testid="create-story-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-white">New Story</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        {/* Story preview with text */}
        <div className="rounded-xl min-h-[200px] flex items-center justify-center mb-3 relative overflow-hidden" style={{ backgroundColor: bgColor }}>
          {file && <div className="absolute inset-0 bg-zinc-800 flex items-center justify-center text-zinc-500 text-sm">{file.name}</div>}
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Text on story..." className="w-full bg-transparent text-white text-center text-xl font-bold outline-none resize-none placeholder:text-white/40 relative z-10 px-4" rows={4} data-testid="story-content-input" />
        </div>
        {/* Caption input */}
        <input type="text" value={caption} onChange={e => setCaption(e.target.value)}
          placeholder="Add a caption (shown below story)..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white outline-none mb-3 placeholder-zinc-500" data-testid="story-caption-input" />
        <div className="flex items-center space-x-2 mb-3">
          {TEXT_BG_COLORS.map(c => (
            <button key={c} onClick={() => setBgColor(c)} className={`w-7 h-7 rounded-full border-2 ${bgColor === c ? 'border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} />
          ))}
        </div>
        <label className="block w-full bg-zinc-800 border border-dashed border-zinc-600 rounded-xl p-4 text-center cursor-pointer mb-3">
          <span className="text-sm text-zinc-400">{file ? file.name : 'Or upload a photo'}</span>
          <input type="file" accept="image/*" className="hidden" onChange={e => setFile(e.target.files[0])} />
        </label>
        <button onClick={handleCreate} disabled={posting || (!content && !file)}
          className="w-full bg-white text-black font-bold rounded-full py-3 text-sm disabled:opacity-30" data-testid="submit-story-btn">
          {posting ? 'Sharing...' : 'Share Story'}
        </button>
      </div>
    </div>
  );
}
